import type { AudioTrackFilters } from '../../api'

export const defaultAudioFilters: AudioTrackFilters = {
  group: undefined,
  page: 1,
  pageSize: 25,
  search: undefined,
  sortBy: 'created_at',
  sortDirection: 'desc',
  sunoPromptBinding: undefined,
  lossless: undefined
}

const validPageSizes = new Set([10, 25, 50, 100])

export const normalizeAudioFilters = (filters: AudioTrackFilters): AudioTrackFilters => {
  const search = filters.search?.trim()
  const labelIds = [...new Set(filters.labelIds ?? [])].filter(Boolean).sort()
  const page = filters.page && filters.page > 0 ? filters.page : defaultAudioFilters.page
  const pageSize = filters.pageSize && validPageSizes.has(filters.pageSize)
    ? filters.pageSize
    : defaultAudioFilters.pageSize
  const sortBy = filters.sortBy ?? defaultAudioFilters.sortBy
  const sortDirection = filters.sortDirection ?? defaultAudioFilters.sortDirection

  return {
    group: filters.group || undefined,
    labelIds: labelIds.length > 0 ? labelIds : undefined,
    page,
    pageSize,
    search: search || undefined,
    sortBy,
    sortDirection,
    sunoPromptBinding: filters.sunoPromptBinding,
    lossless: filters.lossless
  }
}

export const getAudioFiltersKey = (filters: AudioTrackFilters): string => {
  const normalized = normalizeAudioFilters(filters)

  return JSON.stringify({
    group: normalized.group ?? '',
    labelIds: normalized.labelIds ?? [],
    page: normalized.page,
    pageSize: normalized.pageSize,
    search: normalized.search ?? '',
    sortBy: normalized.sortBy,
    sortDirection: normalized.sortDirection,
    sunoPromptBinding: normalized.sunoPromptBinding ?? '',
    lossless: normalized.lossless ?? ''
  })
}

export const areAudioFiltersEqual = (
  left: AudioTrackFilters,
  right: AudioTrackFilters
): boolean => getAudioFiltersKey(left) === getAudioFiltersKey(right)

export const audioFiltersToSearchParams = (filters: AudioTrackFilters): URLSearchParams => {
  const normalized = normalizeAudioFilters(filters)
  const params = new URLSearchParams()

  if (normalized.search) {
    params.set('search', normalized.search)
  }
  if (normalized.group) {
    params.set('group', normalized.group)
  }
  if (normalized.labelIds && normalized.labelIds.length > 0) {
    params.set('labels', normalized.labelIds.join(','))
  }
  if (normalized.page && normalized.page !== defaultAudioFilters.page) {
    params.set('page', String(normalized.page))
  }
  if (normalized.pageSize && normalized.pageSize !== defaultAudioFilters.pageSize) {
    params.set('pageSize', String(normalized.pageSize))
  }
  if (normalized.sortBy && normalized.sortBy !== defaultAudioFilters.sortBy) {
    params.set('sortBy', normalized.sortBy)
  }
  if (normalized.sortDirection && normalized.sortDirection !== defaultAudioFilters.sortDirection) {
    params.set('sortDirection', normalized.sortDirection)
  }
  if (normalized.sunoPromptBinding) {
    params.set('sunoPromptBinding', normalized.sunoPromptBinding)
  }
  if (normalized.lossless) {
    params.set('lossless', normalized.lossless)
  }

  return params
}

export const searchParamsToAudioFilters = (params: URLSearchParams): AudioTrackFilters => {
  const filters: AudioTrackFilters = { ...defaultAudioFilters }

  const search = params.get('search')
  if (search) {
    filters.search = search
  }

  const group = params.get('group')
  if (group) {
    filters.group = group
  }

  const labels = [
    ...params.getAll('labels').flatMap((value) => value.split(',')),
    ...params.getAll('labelIds')
  ].filter(Boolean)
  if (labels.length > 0) {
    filters.labelIds = labels
  }

  const page = params.get('page')
  if (page) {
    const parsed = parseInt(page, 10)
    if (!Number.isNaN(parsed) && parsed > 0) {
      filters.page = parsed
    }
  }

  const pageSize = params.get('pageSize')
  if (pageSize) {
    const parsed = parseInt(pageSize, 10)
    if (!Number.isNaN(parsed) && parsed > 0) {
      filters.pageSize = parsed
    }
  }

  const sortBy = params.get('sortBy')
  if (sortBy === 'created_at' || sortBy === 'duration' || sortBy === 'title') {
    filters.sortBy = sortBy
  }

  const sortDirection = params.get('sortDirection')
  if (sortDirection === 'asc' || sortDirection === 'desc') {
    filters.sortDirection = sortDirection
  }

  const sunoPromptBinding = params.get('sunoPromptBinding')
  if (sunoPromptBinding === 'linked' || sunoPromptBinding === 'unlinked') {
    filters.sunoPromptBinding = sunoPromptBinding
  }

  const lossless = params.get('lossless')
  if (
    lossless === 'has_flac'
    || lossless === 'missing_flac'
  ) {
    filters.lossless = lossless
  }

  return normalizeAudioFilters(filters)
}
